import { Node, mergeAttributes } from '@tiptap/react';

/**
 * Text-bearing block nodes: their editable truth is ProseMirror content, not an
 * attribute (ported from Scilora: richtext-block, heading-block, quote-block,
 * code-block-node, divider-block, unknown-block).
 */

export const RichtextBlock = Node.create({
    name: 'richtextBlock',
    group: 'block',
    content: 'inline*',

    addAttributes() {
        return {
            blockId: { default: null },
            blockMeta: { default: null },
            html: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="richtext-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'richtext-block', class: 'richtext-block' }), 0];
    },
});

export const HeadingBlock = Node.create({
    name: 'headingBlock',
    group: 'block',
    content: 'text*',
    defining: true,

    addAttributes() {
        return {
            blockId: { default: null },
            blockMeta: { default: null },
            level: {
                default: 2,
                parseHTML: (element) => {
                    const match = /^h(\d)$/.exec(element.tagName.toLowerCase());
                    return match ? parseInt(match[1], 10) : 2;
                },
                renderHTML: (attributes) => ({ 'data-level': attributes.level as number }),
            },
        };
    },

    parseHTML() {
        return [1, 2, 3, 4, 5, 6].map((level) => ({ tag: `h${level}[data-type="heading-block"]`, attrs: { level } }));
    },

    renderHTML({ node, HTMLAttributes }) {
        const level = (node.attrs.level as number) || 2;
        return [`h${level}`, mergeAttributes(HTMLAttributes, { 'data-type': 'heading-block', class: 'heading-block' }), 0];
    },

    addKeyboardShortcuts() {
        return {
            'Mod-Alt-1': () => this.editor.commands.updateAttributes('headingBlock', { level: 1 }),
            'Mod-Alt-2': () => this.editor.commands.updateAttributes('headingBlock', { level: 2 }),
            'Mod-Alt-3': () => this.editor.commands.updateAttributes('headingBlock', { level: 3 }),
        };
    },
});

export const QuoteBlock = Node.create({
    name: 'quoteBlock',
    group: 'block',
    content: 'text*',
    defining: true,

    addAttributes() {
        return {
            blockId: { default: null },
            blockMeta: { default: null },
            attribution: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'blockquote[data-type="quote-block"]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        const attribution = node.attrs.attribution as string;
        const attrs = mergeAttributes(HTMLAttributes, { 'data-type': 'quote-block', class: 'quote-block' });
        if (attribution) {
            return ['figure', { class: 'quote-block-wrapper' }, ['blockquote', attrs, 0], ['figcaption', { class: 'quote-attribution' }, attribution]];
        }
        return ['blockquote', attrs, 0];
    },
});

export const CodeBlockNode = Node.create({
    name: 'codeBlockNode',
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    defining: true,

    addAttributes() {
        return {
            blockId: { default: null },
            blockMeta: { default: null },
            language: { default: '' },
            showLineNumbers: { default: false },
            caption: { default: '' },
        };
    },

    parseHTML() {
        return [{ tag: 'pre[data-type="code-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        const language = (HTMLAttributes.language as string) || '';
        return ['pre', mergeAttributes({ 'data-type': 'code-block', class: 'code-block' }, { 'data-language': language }), ['code', HTMLAttributes, 0]];
    },

    addKeyboardShortcuts() {
        return {
            Tab: () => {
                if (this.editor.isActive('codeBlockNode')) {
                    this.editor.commands.insertContent('  ');
                    return true;
                }
                return false;
            },
        };
    },
});

export const DividerBlock = Node.create({
    name: 'dividerBlock',
    group: 'block',
    atom: true,
    draggable: true,

    addAttributes() {
        return { blockId: { default: null }, blockMeta: { default: null } };
    },

    parseHTML() {
        return [{ tag: 'hr[data-type="divider-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['hr', mergeAttributes(HTMLAttributes, { 'data-type': 'divider-block', class: 'divider-block' })];
    },
});

export const UnknownBlock = Node.create({
    name: 'unknownBlock',
    group: 'block',
    atom: true,

    addAttributes() {
        return {
            blockId: { default: null },
            blockMeta: { default: null },
            originalType: { default: '' },
            data: { default: {} },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="unknown-block"]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'unknown-block', class: 'unknown-block' }), `[${(node.attrs.originalType as string) || 'unknown'}]`];
    },
});
